import React, { useState } from 'react';
import theme from './BookameraIntegration.module.scss';
import {
    SimpleCard,
    ValidatingInput,
    ValidationForm,
    ValidationSummary,
} from '@ygtechnologies/bygr-frontend-component-library';
import { deleteConfig, saveConfig } from '@/services/booking';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { AlertBanner, Button, ButtonBar, ConfirmButton } from '@ygtechnologies/rubus-ui';

interface BookameraIntegrationProps {
    projectId: number;
    hasIntegration: boolean | null;
    reloadHasIntegration: () => Promise<void>;
}

const BookameraIntegrationStatus = ({ projectId, hasIntegration, reloadHasIntegration }: BookameraIntegrationProps) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [apikey, setApiKey] = useState('');
    const [companyId, setCompanyId] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(false);

    const save = async () => {
        setIsSaving(true);
        try {
            await saveConfig(projectId, { projectId, userName: username, password, apiKey: apikey, companyId });
        } catch (e) {
            setError(true);
        }
        await reloadHasIntegration();
        setIsSaving(false);
    };
    const deactivate = async () => {
        setIsSaving(true);
        await deleteConfig(projectId);
        await reloadHasIntegration();
        setIsSaving(false);
    };
    return (
        <div>
            {hasIntegration === true ? (
                <>
                    <SimpleCard className={theme.status}>
                        <span className={theme.statusText}>
                            <Trans>Status</Trans>:{' '}
                            <span className={theme.statusActive}>
                                <Trans>Active</Trans>
                            </span>
                        </span>

                        <div className={theme.deactivateButton}>
                            <ConfirmButton
                                variant="danger"
                                size="md"
                                loading={isSaving}
                                onConfirm={deactivate}
                                dialogTitle={<Trans>Are you sure?</Trans>}
                                dialogBody={<Trans>Are you sure you want to deactivate integration?</Trans>}
                                confirmButton={{
                                    variant: 'danger',
                                    children: <Trans>Deactivate</Trans>,
                                }}>
                                <Trans>Deactivate integration</Trans>
                            </ConfirmButton>
                        </div>
                    </SimpleCard>
                </>
            ) : (
                <>
                    <AlertBanner type="info" title={<Trans>Integration not activated</Trans>}>
                        <Trans>
                            The Bokamera integration is not activated. If you want to activate it, you need to input your API
                            key, CompanyId, Username, and Password. You can obtain these from Bokamera.
                        </Trans>
                    </AlertBanner>
                    <ValidationForm onSubmit={save} className="form columns-1">
                        <label>
                            <Trans>API key</Trans> *
                            <ValidatingInput
                                required
                                type="text"
                                className="form-control"
                                placeholder={t`API key`}
                                value={apikey}
                                onChange={(e) => setApiKey(e.target.value)}
                            />
                        </label>
                        <label>
                            <Trans>Company Id</Trans> *
                            <ValidatingInput
                                required
                                type="text"
                                className="form-control"
                                placeholder={t`Company Id`}
                                value={companyId}
                                onChange={(e) => setCompanyId(e.target.value)}
                            />
                        </label>
                        <label>
                            <Trans>User name</Trans> *
                            <ValidatingInput
                                required
                                type="text"
                                className="form-control"
                                placeholder={t`User name`}
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                            />
                        </label>
                        <label>
                            <Trans>Password</Trans> *
                            <ValidatingInput
                                required
                                type="password"
                                className="form-control"
                                placeholder={t`Password`}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </label>
                        <ValidationSummary />
                        {error && (
                            <div>
                                <AlertBanner type="danger" title={<Trans>Error</Trans>}>
                                    <Trans>Wrong username, password, API key or Company id</Trans>
                                </AlertBanner>
                            </div>
                        )}
                        <ButtonBar>
                            <ButtonBar.Right>
                                <Button type="submit" loading={isSaving} variant="primary" className={theme.button}>
                                    <Trans>Activate</Trans>
                                </Button>
                            </ButtonBar.Right>
                        </ButtonBar>
                    </ValidationForm>
                </>
            )}
        </div>
    );
};
export default BookameraIntegrationStatus;
